import { Phone, EnvelopeSimple, MapPin, Clock, ArrowUpRight } from "@phosphor-icons/react";
import { CLINIC, IMAGES } from "../data/site";
import { Reveal, Eyebrow } from "../components/Reveal";
import { EnquiryForm } from "../components/EnquiryForm";

const InfoRow = ({ icon: Icon, label, children, testId }) => (
  <div className="flex gap-4 border-t border-border py-5" data-testid={testId}>
    <Icon size={20} className="mt-0.5 shrink-0 text-primary" />
    <div>
      <p className="text-[11px] uppercase tracking-[0.25em] text-muted-foreground">{label}</p>
      <div className="mt-1.5 text-sm font-light">{children}</div>
    </div>
  </div>
);

export default function Contact() {
  return (
    <main data-testid="contact-page">
      <section className="border-b border-border bg-secondary/40 pb-16 pt-40 sm:pb-24">
        <div className="mx-auto max-w-7xl px-5 sm:px-8">
          <Reveal y={20}>
            <Eyebrow>Contact & Booking</Eyebrow>
            <h1 className="mt-4 max-w-3xl font-display text-5xl font-light leading-[1.02] tracking-tight sm:text-6xl lg:text-7xl">
              Start with a <em className="text-primary">conversation.</em>
            </h1>
            <p className="mt-6 max-w-xl text-base font-light leading-relaxed text-muted-foreground">
              Book a free 15-minute consultation and we'll help you understand which treatment fits your
              needs — no obligation, no pressure.
            </p>
          </Reveal>
        </div>
      </section>

      <section className="mx-auto max-w-7xl px-5 py-20 sm:px-8 sm:py-28">
        <div className="grid grid-cols-1 gap-16 lg:grid-cols-12">
          <Reveal className="lg:col-span-7">
            <div className="border border-border bg-background p-8 sm:p-12" data-testid="contact-form-card">
              <h2 className="font-display text-3xl font-light tracking-tight">Send us an enquiry</h2>
              <p className="mt-2 text-sm font-light text-muted-foreground">
                Your message goes straight to the clinic inbox — we reply personally.
              </p>
              <div className="mt-8">
                <EnquiryForm defaultType="consultation" />
              </div>
            </div>
          </Reveal>

          <div className="lg:col-span-5">
            <Reveal delay={0.1}>
              <InfoRow icon={Phone} label="Call or WhatsApp" testId="contact-phone-row">
                <a href={CLINIC.phoneHref} data-testid="contact-phone-link" className="transition-colors duration-300 hover:text-primary">{CLINIC.phone}</a>
                <span className="mx-2 text-border">|</span>
                <a href={CLINIC.whatsapp} target="_blank" rel="noopener noreferrer" data-testid="contact-whatsapp-link" className="transition-colors duration-300 hover:text-primary">WhatsApp us</a>
              </InfoRow>
              <InfoRow icon={EnvelopeSimple} label="Email" testId="contact-email-row">
                <a href={`mailto:${CLINIC.email}`} data-testid="contact-email-link" className="transition-colors duration-300 hover:text-primary">{CLINIC.email}</a>
              </InfoRow>
              <InfoRow icon={MapPin} label="Derby Clinic" testId="contact-address-row">
                <p>{CLINIC.address}</p>
                <a href={CLINIC.directionsUrl} target="_blank" rel="noopener noreferrer" data-testid="contact-directions-link" className="mt-1 inline-flex items-center gap-1 text-xs uppercase tracking-[0.2em] text-primary">
                  Get directions <ArrowUpRight size={12} />
                </a>
              </InfoRow>
              <InfoRow icon={Clock} label="Opening hours" testId="contact-hours-row">
                <p className="mt-1 text-[11px] uppercase tracking-[0.2em] text-muted-foreground">Draycott, Derby</p>
                {CLINIC.hoursDerby.map((h) => (
                  <p key={h.day} className="flex justify-between gap-8 py-0.5"><span>{h.day}</span><span className="text-muted-foreground">{h.time}</span></p>
                ))}
                <p className="mt-3 text-[11px] uppercase tracking-[0.2em] text-muted-foreground">Manor Dental, Sutton in Ashfield</p>
                {CLINIC.hoursNottingham.map((h) => (
                  <p key={h.day} className="flex justify-between gap-8 py-0.5"><span>{h.day}</span><span className="text-muted-foreground">{h.time}</span></p>
                ))}
              </InfoRow>
              <div className="border-t border-border pt-6">
                <a
                  href={`${CLINIC.whatsapp}?text=${encodeURIComponent("Hello DK Aesthetics, I'd like to book an appointment.")}`}
                  target="_blank"
                  rel="noopener noreferrer"
                  data-testid="contact-book-whatsapp-button"
                  className="flex items-center justify-center gap-2 bg-[#25D366] px-6 py-4 text-xs font-medium uppercase tracking-[0.25em] text-white transition-colors duration-300 hover:bg-[#1fb959]"
                >
                  Book via WhatsApp
                </a>
                <a
                  href={`mailto:${CLINIC.email}?subject=${encodeURIComponent("Appointment booking")}`}
                  data-testid="contact-book-email-button"
                  className="mt-3 flex items-center justify-center gap-2 border border-foreground px-6 py-4 text-xs font-medium uppercase tracking-[0.25em] transition-colors duration-300 hover:bg-foreground hover:text-background"
                >
                  Book via email
                </a>
              </div>
            </Reveal>
          </div>
        </div>
      </section>

      <section className="border-t border-border" data-testid="contact-map-section">
        <div className="mx-auto max-w-7xl px-5 py-16 sm:px-8 sm:py-20">
          <Reveal>
            <Eyebrow>Find us</Eyebrow>
            <h2 className="mt-4 font-display text-4xl font-light tracking-tight sm:text-5xl">
              Collis House, <em className="text-primary">Draycott.</em>
            </h2>
            <p className="mt-4 max-w-xl text-base font-light leading-relaxed text-muted-foreground">
              Easy parking right outside the door. Pan and zoom the map to plan your visit.
            </p>
          </Reveal>
          <Reveal delay={0.1}>
            <div className="mt-10 overflow-hidden border border-border" data-testid="contact-map-frame">
              <iframe
                title="DK Aesthetics — Collis House, Town End Road, Draycott, Derby DE72 3PT"
                src="https://www.google.com/maps?q=Collis+House,+Town+End+Road,+Draycott,+Derby+DE72+3PT&output=embed"
                className="h-[420px] w-full border-0"
                loading="lazy"
                allowFullScreen
                referrerPolicy="no-referrer-when-downgrade"
              />
            </div>
          </Reveal>
        </div>
      </section>

      <section className="border-t border-border" data-testid="contact-clinic-image-section">
        <div className="grid grid-cols-1 md:grid-cols-2">
          <img src={IMAGES.exterior} alt="DK Cosmetic Clinic exterior, Draycott" className="aspect-[16/10] h-full w-full object-cover" />
          <img src={IMAGES.treatmentRoom} alt="Treatment room at DK Aesthetics" className="aspect-[16/10] h-full w-full object-cover" />
        </div>
      </section>
    </main>
  );
}
